import { h, clear, toast, showModal, confirmModal, formatDate, fromIsoToLocalInput, toIsoLocal, fold, emptyState } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { Plus, Users, Pencil, Check, XCircle, Clock, MapPin, Monitor, Globe, List, Filter, X } from 'lucide';
import { Calendar as CalendarIcon } from 'lucide';
import { Calendar as FullCalendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import esLocale from '@fullcalendar/core/locales/es.js';
import { SessionAPI, EnrollmentAPI, ServiceAPI, InstructorAPI } from '../services/endpoints.js';
import { showFieldErrors } from '../utils/validators.js';
import { Avatar, DEFAULT_AVATAR_COLOR } from '../components/avatar.js';
import { EventCard, EventListGrouped, PageTabs, SearchBar, SegmentedControl, formatTime } from '../components/ui.js';
import { SkeletonTable } from '../components/skeleton.js';

const MODALITY_LABELS = { virtual: 'Virtual', in_person: 'Presencial' };
const MODALITY_ICONS = { virtual: Monitor, in_person: MapPin };

const statusLabel = (s) => ({ scheduled: 'Programada', completed: 'Completada', cancelled: 'Cancelada' }[s] || s);
const statusBadgeClass = (s) => ({ scheduled: 'badge pending', completed: 'badge completed', cancelled: 'badge cancelled' }[s] || 'badge');

const attendanceBadge = (status) => {
  if (!status) return null;
  return h('span', { class: `badge ${status === 'attended' ? 'success' : 'danger'}` }, status === 'attended' ? 'Asistió' : 'Ausente');
};

const groupByDate = (sessions) => {
  const map = {};
  for (const s of sessions) {
    const d = s.start_datetime ? new Date(s.start_datetime) : null;
    if (!d || Number.isNaN(d.getTime())) continue;
    const day = d.toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' });
    if (!map[day]) map[day] = [];
    map[day].push(s);
  }
  return Object.entries(map).map(([key, items]) => ({ dateLabel: key, items }));
};

export const SessionsPage = async (user) => {
  const view = document.getElementById('view');
  clear(view);

  const isBeneficiary = user.role === 'beneficiary';
  const isInstructor = user.role === 'instructor';
  const isAdmin = user.role === 'admin';

  let allSessions = [];
  let availableSessions = [];
  let myEnrollments = [];
  let services = [];
  let beneficiaryTab = 'available';
  let currentInstructorId = null;

  let statusFilter = '';
  let searchQ = '';
  let viewMode = 'list';
  let calendarInstance = null;
  let calendarMount = null;

  view.appendChild(h('div', { class: 'page-title' },
    h('h1', {}, 'Sesiones'),
  ));

  const skeletonWrap = h('div', { class: 'card' });
  skeletonWrap.appendChild(h('div', { class: 'table-wrap' }, h('table', { class: 'table' },
    h('thead', {}, h('tr', {},
      h('th', {}, 'Servicio'),
      h('th', {}, 'Instructor'),
      h('th', {}, 'Fecha'),
      h('th', {}, 'Estado'),
      h('th', {}, ''),
    )),
    SkeletonTable(5, 5),
  )));
  view.appendChild(skeletonWrap);

  await loadData();
  skeletonWrap.remove();

  async function loadData() {
    try {
      if (isBeneficiary) {
        const [availRes, enrollRes] = await Promise.all([
          SessionAPI.listAvailable(),
          EnrollmentAPI.listMine(),
        ]);
        availableSessions = availRes.data || [];
        myEnrollments = enrollRes.data || [];
      } else {
        const [sessRes, svcRes, instrRes] = await Promise.all([
          SessionAPI.list(),
          ServiceAPI.list(),
          isInstructor ? InstructorAPI.list() : Promise.resolve({ data: [] }),
        ]);
        allSessions = sessRes.data || [];
        services = svcRes.data || [];
        if (isInstructor && instrRes) {
          const instList = instrRes.data || [];
          const me = instList.find((i) => Number(i.user_id) === Number(user.id));
          currentInstructorId = me ? Number(me.id) : null;
        }
      }
      render();
    } catch (err) { toast(err.message, 'error'); }
  }

  function toggleView(mode) {
    viewMode = mode;
    render();
  }

  function render() {
    clear(view);
    view.appendChild(h('div', { class: 'page-title' },
      h('h1', {}, 'Sesiones'),
      h('div', { class: 'btn-row' },
        !isBeneficiary
          ? h('button', { class: 'btn', onclick: openCreateModal }, h('span', { html: iconSvg(Plus, 16) }), 'Crear sesión')
          : null,
        !isBeneficiary ? h('span', { class: 'sep' }) : null,
        h('button', { class: `btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'}`, onclick: () => toggleView('list') },
          h('span', { html: iconSvg(List, 14) }), 'Lista'),
        h('button', { class: `btn btn-sm ${viewMode === 'calendar' ? 'btn-primary' : 'btn-ghost'}`, onclick: () => toggleView('calendar') },
          h('span', { html: iconSvg(CalendarIcon, 14) }), 'Calendario'),
      ),
    ));
    if (isBeneficiary) renderBeneficiaryView();
    else if (viewMode === 'calendar') renderCalendarView();
    else renderAdminView();
  }

  // --- Beneficiary view ---

  function renderBeneficiaryView() {
    view.appendChild(PageTabs({
      tabs: [
        { id: 'available', label: 'Disponibles' },
        { id: 'mine', label: 'Mis inscripciones' },
      ],
      activeTab: beneficiaryTab,
      onTabChange: (id) => { beneficiaryTab = id; render(); },
    }));

    if (beneficiaryTab === 'available') renderAvailableSessions();
    else renderMyEnrollments();
  }

  function renderAvailableSessions() {
    const enrolledIds = new Set(myEnrollments.filter((e) => e.status === 'enrolled').map((e) => Number(e.session_id)));

    function renderCard(s) {
      const isEnrolled = enrolledIds.has(Number(s.id));
      return EventCard({
        time: formatTime(s.start_datetime),
        title: s.service_name || '—',
        hostName: s.instructor_name,
        hostColor: DEFAULT_AVATAR_COLOR,
        meta: [
          h('span', {}, h('span', { html: iconSvg(Clock, 12) }), ` ${s.duration_minutes} min`),
          MODALITY_ICONS[s.modality] ? h('span', {}, h('span', { html: iconSvg(MODALITY_ICONS[s.modality], 12) }), ` ${MODALITY_LABELS[s.modality]}`) : null,
        ],
        badge: h('div', { class: 'row gap-sm' },
        h('span', { class: statusBadgeClass(s.session_status || s.status) }, statusLabel(s.session_status || s.status)),
          h('span', { class: 'badge info text-xs' }, `${s.occupied_slots || 0}/${s.max_capacity} cupos`),
        ),
        thumbIcon: MODALITY_ICONS[s.modality],
        thumbColor: s.modality === 'virtual' ? '#4fc1e9' : '#10b981',
        actions: !isEnrolled && (s.occupied_slots || 0) < s.max_capacity
          ? [h('button', { class: 'btn btn-sm btn-success', onclick: (e) => { e.stopPropagation(); enrollInSession(s); } }, 'Inscribirse')]
          : isEnrolled
            ? [h('span', { class: 'badge success' }, 'Inscrito')]
            : [],
        onclick: () => openSessionDetail(s),
      });
    }

    const groups = groupByDate(availableSessions).map((g) => ({
      dateLabel: g.dateLabel,
      items: g.items.map(renderCard),
    }));

    const content = h('div', {},
      h('div', { class: 'row-between' },
        h('h2', { style: { margin: 0 } }, 'Sesiones disponibles'),
        h('span', { class: 'muted text-sm' }, `${availableSessions.length} sesión${availableSessions.length === 1 ? '' : 'es'}`),
      ),
      !availableSessions.length
        ? emptyState('No hay sesiones disponibles en este momento.')
        : EventListGrouped({ groups, groupKey: 'dateLabel' }),
    );
    view.appendChild(content);
  }

  function renderMyEnrollments() {
    if (!myEnrollments.length) {
      view.appendChild(emptyState('No estás inscrito en ninguna sesión.'));
      return;
    }

    function renderCard(e) {
      return EventCard({
        time: formatTime(e.start_datetime),
        title: e.service_name || '—',
        hostName: e.instructor_name,
        hostColor: DEFAULT_AVATAR_COLOR,
        meta: [
          h('span', {}, h('span', { html: iconSvg(Clock, 12) }), ` ${e.duration_minutes} min`),
          e.modality && MODALITY_ICONS[e.modality]
            ? h('span', {}, h('span', { html: iconSvg(MODALITY_ICONS[e.modality], 12) }), ` ${MODALITY_LABELS[e.modality]}`)
            : null,
        ],
        badge: h('div', { class: 'row gap-sm' },
          h('span', { class: statusBadgeClass(e.session_status) }, statusLabel(e.session_status)),
          e.status === 'attended' || e.status === 'no_show' ? attendanceBadge(e.status) : null,
          h('span', { class: 'badge info text-xs' }, `${e.occupied_slots || 0}/${e.max_capacity} cupos`),
        ),
        thumbIcon: e.modality ? MODALITY_ICONS[e.modality] : null,
        thumbColor: e.modality === 'virtual' ? '#4fc1e9' : e.modality === 'in_person' ? '#10b981' : '#5e9beb',
        actions: e.status === 'enrolled'
          ? [h('button', { class: 'btn btn-ghost btn-sm', onclick: (ev) => { ev.stopPropagation(); cancelMyEnrollment(e.id); } }, h('span', { html: iconSvg(XCircle, 14) }), 'Cancelar')]
          : [],
        onclick: () => openSessionDetail(e),
      });
    }

    const groups = groupByDate(myEnrollments).map((g) => ({
      dateLabel: g.dateLabel,
      items: g.items.map(renderCard),
    }));

    view.appendChild(h('div', {},
      h('h2', { style: { margin: 0 } }, 'Tus inscripciones'),
      EventListGrouped({ groups, groupKey: 'dateLabel' }),
    ));
  }

  // --- Admin / Instructor view ---

  function renderAdminView() {
    const filtered = allSessions.filter((s) => {
      if (searchQ) {
        const q = fold(searchQ);
        if (!fold(s.service_name || '').includes(q) && !fold(s.instructor_name || '').includes(q)) return false;
      }
      if (statusFilter && s.status !== statusFilter) return false;
      return true;
    });

    view.appendChild(h('div', { class: 'toolbar' },
      SearchBar({ placeholder: 'Buscar por servicio o instructor…', oninput: (e) => { searchQ = e.target.value; render(); } }),
      h('span', { class: 'filter-label' }, 'Estado'),
      PageTabs({
        tabs: [
          { id: '', label: 'Todas' },
          { id: 'scheduled', label: 'Programada' },

          { id: 'completed', label: 'Completada' },
          { id: 'cancelled', label: 'Cancelada' },
        ],
        activeTab: statusFilter,
        onTabChange: (id) => { statusFilter = id; render(); },
      }),
    ));

    function canManageSession(s) {
      if (isAdmin) return true;
      if (isInstructor) {
        if (Number(currentInstructorId) === Number(s.instructor_id)) return true;
        const svc = services.find(svc => Number(svc.id) === Number(s.service_id));
        if (svc && svc.instructor_ids && svc.instructor_ids.some(id => Number(id) === Number(currentInstructorId))) return true;
      }
      return false;
    }

    function renderCard(s) {
      const canManage = canManageSession(s);
      const actions = [];
      if (canManage && s.status === 'scheduled') {
        actions.push(h('button', { class: 'btn btn-ghost btn-sm', onclick: (e) => { e.stopPropagation(); openEditModal(s); } }, h('span', { html: iconSvg(Pencil, 14) }), 'Editar'));
      }
    const sessionStatus = s.session_status || s.status;
    if (canManage && sessionStatus === 'scheduled') {
        actions.push(h('button', { class: 'btn btn-ghost btn-sm', onclick: (e) => { e.stopPropagation(); openEnrollments(s); } }, h('span', { html: iconSvg(Users, 14) }), 'Inscripciones'));
      }
      return EventCard({
        time: formatTime(s.start_datetime),
        title: s.service_name || '—',
        hostName: s.instructor_name,
        hostColor: DEFAULT_AVATAR_COLOR,
        meta: [
          h('span', {}, h('span', { html: iconSvg(Clock, 12) }), ` ${s.duration_minutes} min`),
          MODALITY_ICONS[s.modality] ? h('span', {}, h('span', { html: iconSvg(MODALITY_ICONS[s.modality], 12) }), ` ${MODALITY_LABELS[s.modality]}`) : null,
        ],
        badge: h('span', { class: statusBadgeClass(s.status) }, statusLabel(s.status)),
        thumbIcon: MODALITY_ICONS[s.modality],
        thumbColor: s.modality === 'virtual' ? '#4fc1e9' : '#10b981',
        actions,
        onclick: () => openSessionDetail(s),
      });
    }

    const groups = groupByDate(filtered).map((g) => ({
      dateLabel: g.dateLabel,
      items: g.items.map(renderCard),
    }));

    view.appendChild(h('div', {},
      h('div', { class: 'row-between' },
        h('h2', { style: { margin: 0 } }, 'Todas las sesiones'),
        h('span', { class: 'muted text-sm' }, `${filtered.length} sesión${filtered.length === 1 ? '' : 'es'}`),
      ),
      !filtered.length
        ? emptyState('No hay sesiones que coincidan.')
        : EventListGrouped({ groups, groupKey: 'dateLabel' }),
    ));
  }

  // --- Create / Edit modals ---

  function openCreateModal() {
    const form = h('div', { class: 'form-create' },
      h('h3', {}, 'Nueva sesión'),
      h('p', { class: 'muted text-sm' }, 'Crea una clase o encuentro para un servicio. Los beneficiarios podrán inscribirse si hay cupo disponible.'),
      h('div', { class: 'form-section' },
        h('label', {}, 'Servicio'),
        h('select', { name: 'service_id', required: true },
          h('option', { value: '' }, 'Seleccionar servicio…'),
          ...services.map((svc) => h('option', { value: svc.id }, svc.name)),
        ),
        h('label', {}, 'Fecha y hora de inicio'),
        h('input', { type: 'datetime-local', name: 'start_datetime', required: true }),
        h('div', { class: 'grid grid-2' },
          h('div', {},
            h('label', {}, 'Duración (minutos)'),
            h('input', { type: 'number', name: 'duration_minutes', min: '15', max: '480', value: '60', required: true }),
          ),
          h('div', {},
            h('label', {}, 'Cupo máximo'),
            h('input', { type: 'number', name: 'max_capacity', min: '1', value: '15', required: true }),
          ),
        ),
      ),
      h('div', { class: 'form-section' },
        h('label', {}, 'Modalidad'),
        h('div', { style: { marginTop: '4px' } },
          SegmentedControl({
            options: [
              { value: 'in_person', label: 'Presencial', icon: MapPin },
              { value: 'virtual', label: 'Virtual', icon: Monitor },
            ],
            value: 'in_person',
            onChange: (val) => { form.querySelector('[name="modality"]').value = val; },
          }),
        ),
        h('input', { type: 'hidden', name: 'modality', value: 'in_person' }),
        h('label', {}, 'Detalles (opcional)'),
        h('textarea', { name: 'session_details', placeholder: 'Enlace de reunión, dirección, materiales necesarios…' }),
      ),
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-block', onclick: submitCreate }, h('span', { html: iconSvg(Plus, 16) }), 'Crear sesión'),
      ),
    );
    const m = showModal(form);
    form.closest('.modal')?.classList.add('modal-sheet');

    async function submitCreate() {
      const data = {};
      for (const el of form.querySelectorAll('[name]')) {
        if (el.type === 'number') data[el.name] = el.value ? Number(el.value) : null;
        else data[el.name] = el.value || null;
      }
      if (!data.service_id) { showFieldErrors(form, { service_id: 'Selecciona un servicio' }); return; }
      if (!data.start_datetime) { showFieldErrors(form, { start_datetime: 'Selecciona fecha y hora' }); return; }
      if (!isInstructor) {
        data.instructor_id = user.id;
      }
      data.start_datetime = toIsoLocal(data.start_datetime);
      try {
        await SessionAPI.create(data);
        toast('Sesión creada', 'success');
        m.close();
        await loadData();
      } catch (err) { toast(err.message, 'error'); }
    }
  }

  function openEditModal(session) {
    const form = h('div', { class: 'form-create' },
      h('h3', {}, `Editar sesión — ${session.service_name}`),
      h('p', { class: 'muted text-sm' }, 'Modifica los datos de la sesión.'),
      h('div', { class: 'form-section' },
        h('label', {}, 'Fecha y hora de inicio'),
        h('input', { type: 'datetime-local', name: 'start_datetime', value: fromIsoToLocalInput(session.start_datetime), required: true }),
        h('div', { class: 'grid grid-2' },
          h('div', {},
            h('label', {}, 'Duración (minutos)'),
            h('input', { type: 'number', name: 'duration_minutes', min: '15', max: '480', value: String(session.duration_minutes), required: true }),
          ),
          h('div', {},
            h('label', {}, 'Capacidad máxima'),
            h('input', { type: 'number', name: 'max_capacity', min: '1', value: String(session.max_capacity), required: true }),
          ),
        ),
      ),
      h('div', { class: 'form-section' },
        h('label', {}, 'Modalidad'),
        h('div', { style: { marginTop: '4px' } },
          SegmentedControl({
            options: [
              { value: 'in_person', label: 'Presencial', icon: MapPin },
              { value: 'virtual', label: 'Virtual', icon: Monitor },
            ],
            value: session.modality || 'in_person',
            onChange: (val) => { form.querySelector('[name="modality"]').value = val; },
          }),
        ),
        h('input', { type: 'hidden', name: 'modality', value: session.modality || 'in_person' }),
        h('label', {}, 'Detalles (opcional)'),
        h('textarea', { name: 'session_details' }, session.session_details || ''),
      ),
      h('div', { class: 'btn-row mt-16' },
        h('button', { class: 'btn btn-block', onclick: submitEdit }, h('span', { html: iconSvg(Pencil, 16) }), 'Guardar cambios'),
      ),
    );
    const m = showModal(form);
    form.closest('.modal')?.classList.add('modal-sheet');

    async function submitEdit() {
      const data = {};
      for (const el of form.querySelectorAll('[name]')) {
        if (el.type === 'number') data[el.name] = el.value ? Number(el.value) : null;
        else data[el.name] = el.value || null;
      }
      if (!data.start_datetime) { showFieldErrors(form, { start_datetime: 'Selecciona fecha y hora' }); return; }
      data.start_datetime = toIsoLocal(data.start_datetime);
      try {
        await SessionAPI.update(session.id, data);
        toast('Sesión actualizada', 'success');
        m.close();
        await loadData();
      } catch (err) { toast(err.message, 'error'); }
    }
  }

  // --- Session detail modal ---

  function openSessionDetail(s) {
    const enrolledCount = s.occupied_slots || 0;
    const modIcon = MODALITY_ICONS[s.modality];
    const isAdminUser = user.role === 'admin';
    const isInstr = user.role === 'instructor' && (
      Number(currentInstructorId) === Number(s.instructor_id) ||
      (() => {
        const svc = services.find(svc => Number(svc.id) === Number(s.service_id));
        return svc && svc.instructor_ids && svc.instructor_ids.some(id => Number(id) === Number(currentInstructorId));
      })()
    );
    const canManage = isAdminUser || isInstr;

    const main = h('div', { class: 'detail-main' },
      h('h2', { class: 'detail-hero-title' }, s.service_name || '—'),
      h('div', { class: 'detail-meta-row' },
        h('span', {}, h('span', { html: iconSvg(CalendarIcon, 14) }), ` ${formatDate(s.start_datetime)}`),
        h('span', {}, h('span', { html: iconSvg(Clock, 14) }), ` ${s.duration_minutes} min`),
        modIcon ? h('span', {}, h('span', { html: iconSvg(modIcon, 14) }), ` ${MODALITY_LABELS[s.modality]}`) : null,
      ),
      h('div', { class: 'row gap-sm' },
        h('span', { class: statusBadgeClass(s.status) }, statusLabel(s.status)),
        h('span', { class: 'badge info' }, `${enrolledCount}/${s.max_capacity} cupos`),
      ),
      h('div', { class: 'detail-section' },
        h('p', { class: 'detail-section-title' }, 'Instructor'),
        h('div', { class: 'event-card-host' },
          Avatar(s.instructor_name, DEFAULT_AVATAR_COLOR, 32),
          h('span', {}, s.instructor_name || '—'),
        ),
      ),
      s.session_details ? h('div', { class: 'detail-section' },
        h('p', { class: 'detail-section-title' }, 'Acerca de la sesión'),
        h('p', {}, s.session_details),
      ) : null,
    );

    const side = h('div', { class: 'detail-side' },
      h('h4', {}, 'Asistir'),
      h('div', { class: 'rsvp-box' },
        h('p', {}, `${enrolledCount} de ${s.max_capacity} cupos ocupados`),
        canManage && (s.session_status || s.status) === 'scheduled'
          ? h('button', { class: 'btn btn-accent btn-block', id: 'detail-edit-btn' }, 'Editar sesión')
          : null,
      ),
    );

    const content = h('div', { class: 'detail-layout' }, main, side);
    const m = showModal(content);
    content.closest('.modal')?.classList.add('modal-wide');

    const editBtn = side.querySelector('#detail-edit-btn');
    if (editBtn) editBtn.onclick = () => { m.close(); openEditModal(s); };

    const footer = h('div', { class: 'btn-row mt-16' });
    if (canManage && s.status === 'scheduled') {
      footer.appendChild(h('button', { class: 'btn btn-success btn-sm', onclick: () => { m.close(); completeSession(s); } }, 'Completar'));
      footer.appendChild(h('button', { class: 'btn btn-danger btn-sm', onclick: () => { m.close(); cancelSession(s); } }, 'Cancelar sesión'));
    }
    if (canManage || isAdminUser) {
      footer.appendChild(h('button', { class: 'btn btn-ghost btn-sm', onclick: () => { m.close(); openEnrollments(s); } }, 'Ver inscripciones'));
    }
    if (footer.children.length) main.appendChild(footer);
  }

  // --- Enrollments modal ---

  async function openEnrollments(session) {
    let enrollments = [];
    try {
      const res = await EnrollmentAPI.listBySession(session.id);
      enrollments = res.data || [];
    } catch (err) { toast(err.message, 'error'); return; }

    const content = h('div', { class: 'form-create' },
      h('h3', {}, `Inscripciones — ${session.service_name}`),
      h('p', { class: 'muted text-sm' }, `${formatDate(session.start_datetime)} · ${session.occupied_slots || 0}/${session.max_capacity} cupos`),
      h('div', { class: 'form-section' },
        !enrollments.length
          ? h('div', { class: 'table-empty' }, 'No hay beneficiarios inscritos.')
          : (() => {
              const canRecord = session.status === 'scheduled';
              const wrap = h('div', { class: 'table-wrap' });
              wrap.appendChild(h('table', { class: 'table' },
                h('thead', {}, h('tr', {},
                  h('th', {}, 'Beneficiario'),
                  h('th', {}, 'Correo'),
                  h('th', {}, 'Estado'),
                  h('th', {}, canRecord ? 'Asistencia' : ''),
                )),
                h('tbody', {}, ...enrollments.map((e) => h('tr', {},
                  h('td', {}, h('strong', {}, e.beneficiary_name || '—')),
                  h('td', { class: 'muted' }, e.beneficiary_email || '—'),
                  h('td', {},
                    e.status === 'cancelled'
                      ? h('span', { class: 'badge cancelled' }, 'Cancelada')
                      : e.status === 'attended'
                        ? h('span', { class: 'badge success' }, 'Asistió')
                        : e.status === 'no_show'
                          ? h('span', { class: 'badge danger' }, 'Ausente')
                          : h('span', { class: 'badge active' }, 'Activa'),
                  ),
                  h('td', {},
                    canRecord && e.status === 'enrolled'
                      ? h('span', { class: 'row gap-sm', style: { flexWrap: 'nowrap' } },
                          h('button', { class: 'btn btn-sm btn-success', onclick: () => recordAttend(e, 'attended') },
                            h('span', { html: iconSvg(Check, 14) }), 'Asistió'),
                          h('button', { class: 'btn btn-ghost btn-sm', onclick: () => recordAttend(e, 'no_show') },
                            h('span', { html: iconSvg(XCircle, 14) }), 'Ausente'),
                        )
                      : e.status === 'attended' || e.status === 'no_show'
                        ? attendanceBadge(e.status)
                        : null,
                  ),
                ))),
              ));
              return wrap;
            })(),
      ),
    );
    const m = showModal(content);
    content.closest('.modal')?.classList.add('modal-sheet');

    async function recordAttend(enrollment, status) {
      try {
        await EnrollmentAPI.recordAttendance(enrollment.id, status);
        toast('Asistencia registrada', 'success');
        m.close();
        openEnrollments(session);
      } catch (err) { toast(err.message, 'error'); }
    }
  }

  // --- Actions ---

  async function enrollInSession(s) {
    try {
      await EnrollmentAPI.enroll(s.id);
      toast('Inscripción realizada', 'success');
      await loadData();
    } catch (err) { toast(err.message, 'error'); }
  }

  async function cancelMyEnrollment(enrollmentId) {
    const ok = await confirmModal('¿Cancelar tu inscripción?');
    if (!ok) return;
    try {
      await EnrollmentAPI.cancel(enrollmentId);
      toast('Inscripción cancelada', 'success');
      await loadData();
    } catch (err) { toast(err.message, 'error'); }
  }

  async function cancelSession(s) {
    const ok = await confirmModal(`¿Cancelar la sesión "${s.service_name}" del ${formatDate(s.start_datetime)}?`);
    if (!ok) return;
    try {
      await SessionAPI.cancel(s.id);
      toast('Sesión cancelada', 'success');
      await loadData();
    } catch (err) { toast(err.message, 'error'); }
  }

  async function completeSession(s) {
    const ok = await confirmModal(
      `¿Marcar como completada la sesión "${s.service_name}" del ${formatDate(s.start_datetime)}?`,
      { confirmText: 'Completar', variant: 'success' }
    );
    if (!ok) return;
    try {
      await SessionAPI.complete(s.id);
      toast('Sesión completada', 'success');
      await loadData();
    } catch (err) { toast(err.message, 'error'); }
  }

  // --- Calendar view ---

  function renderCalendarView() {
    if (calendarMount) {
      view.appendChild(calendarMount);
      return;
    }

    calendarMount = h('div', { class: 'card calendar-card' });
    view.appendChild(calendarMount);

    const mount = h('div', { id: 'fc-mount' });
    calendarMount.appendChild(mount);

    calendarInstance = new FullCalendar(mount, {
      plugins: [dayGridPlugin, listPlugin],
      locale: esLocale,
      initialView: 'dayGridMonth',
      firstDay: 1,
      height: 680,
      headerToolbar: { left: 'prev,next today', center: 'title', right: 'dayGridMonth,listMonth' },
      buttonText: { today: 'Hoy', month: 'Mes', list: 'Lista' },
      events: allSessions
        .filter((s) => s.status !== 'cancelled')
        .map((s) => {
          const start = new Date(s.start_datetime);
          const end = new Date(start.getTime() + (s.duration_minutes || 60) * 60000);
          return {
            id: `ses-${s.id}`,
            title: s.service_name || 'Sesión',
            start: start.toISOString(),
            end: end.toISOString(),
            classNames: ['fc-evt-session'],
            extendedProps: { id: s.id },
          };
        }),
      eventDidMount: (info) => {
        info.el.classList.add(`fc-evt-${info.event.extendedProps.status || 'default'}`);
      },
      eventClick: (info) => {
        const s = allSessions.find((x) => Number(x.id) === Number(info.event.extendedProps.id));
        if (s) openSessionDetail(s);
      },
    });
    calendarInstance.render();
  }
};
